import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type DocumentData,
  type QuerySnapshot,
} from "firebase/firestore";
import Sidebar from "./Sidebar";
import { trackPageView } from "../../analytics/tracker";
import { useAdminAuth } from "../../auth/AdminAuthContext";
import { db } from "../../firebase";
import { ContentAPI } from "../../api/content";
import { assetUrl } from "../../api/http";
import { PromotionsAdminAPI } from "../../api/promotions";

type SupportThreadSummary = {
  unreadByAdmin?: boolean;
  unreadCountByAdmin?: number;
};

const EMAIL_CAPTURE_LAST_SEEN_KEY = "admin_marketing_email_capture_last_seen_at";
const EMAIL_CAPTURE_SEEN_EVENT = "admin-email-captures-seen";

const getUnreadSupportCount = (thread: SupportThreadSummary): number => {
  const unreadCount = Number(thread.unreadCountByAdmin);
  if (Number.isFinite(unreadCount) && unreadCount > 0) {
    return Math.max(0, Math.floor(unreadCount));
  }
  return thread.unreadByAdmin ? 1 : 0;
};

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [emailLeadUnreadCount, setEmailLeadUnreadCount] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 899px)").matches
      : false
  );
  const [brand, setBrand] = useState({
    siteName: "BeyondPendants",
    logoUrl: "",
  });
  const { pathname, search } = useLocation();
  const { user, logout, firebaseReady, firebaseUid } = useAdminAuth();

  useEffect(() => {
    try {
      trackPageView(pathname + (search || ""));
    } catch {}
  }, [pathname, search]);

  useEffect(() => {
    if (!firebaseReady) {
      setSupportUnreadCount(0);
      return;
    }

    let unsub: (() => void) | undefined;

    const applySnapshot = (snapshot: QuerySnapshot<DocumentData>) => {
      let nextUnreadCount = 0;
      snapshot.forEach((docSnap) => {
        nextUnreadCount += getUnreadSupportCount(
          docSnap.data() as SupportThreadSummary,
        );
      });
      setSupportUnreadCount(nextUnreadCount);
    };

    const subscribeUnordered = () => {
      unsub = onSnapshot(collection(db, "supportThreads"), applySnapshot);
    };

    const orderedQuery = query(
      collection(db, "supportThreads"),
      orderBy("lastMessageAt", "desc"),
    );

    unsub = onSnapshot(
      orderedQuery,
      applySnapshot,
      () => {
        subscribeUnordered();
      },
    );

    return () => {
      if (unsub) unsub();
    };
  }, [firebaseReady]);

  useEffect(() => {
    if (!firebaseReady || !firebaseUid || typeof document === "undefined") {
      return;
    }

    const presenceRef = doc(db, "supportPresence", firebaseUid);
    let heartbeatTimer: number | null = null;

    const writePresence = (online: boolean) =>
      setDoc(
        presenceRef,
        {
          online,
          app: "admin-panel",
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      ).catch(() => {});

    const syncPresence = () => {
      const visible = document.visibilityState !== "hidden";
      if (heartbeatTimer) {
        window.clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }

      void writePresence(visible);
      if (!visible) return;

      heartbeatTimer = window.setInterval(() => {
        void writePresence(true);
      }, 25000);
    };

    syncPresence();
    document.addEventListener("visibilitychange", syncPresence);

    return () => {
      if (heartbeatTimer) {
        window.clearInterval(heartbeatTimer);
      }
      document.removeEventListener("visibilitychange", syncPresence);
      void writePresence(false);
    };
  }, [firebaseReady, firebaseUid]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const baseTitle = "BeyondPendants Admin Panel";
    const totalUnread = supportUnreadCount + emailLeadUnreadCount;
    document.title = totalUnread > 0
      ? `(${totalUnread}) ${baseTitle}`
      : baseTitle;
    return () => {
      document.title = baseTitle;
    };
  }, [emailLeadUnreadCount, supportUnreadCount]);

  useEffect(() => {
    let cancelled = false;

    const refreshEmailLeadUnread = async () => {
      try {
        const result = await PromotionsAdminAPI.listEmailCaptures({ limit: 100 });
        if (cancelled) return;
        const newestCreatedAt = result.summary?.newestCreatedAt || "";
        if (!newestCreatedAt || typeof window === "undefined") {
          setEmailLeadUnreadCount(0);
          return;
        }
        const stored = window.localStorage.getItem(EMAIL_CAPTURE_LAST_SEEN_KEY) || "";
        if (!stored) {
          window.localStorage.setItem(EMAIL_CAPTURE_LAST_SEEN_KEY, newestCreatedAt);
          setEmailLeadUnreadCount(0);
          return;
        }
        const storedMs = new Date(stored).getTime();
        const nextCount = (result.items || []).filter((item) => {
          const createdMs = new Date(item.createdAt || "").getTime();
          return Number.isFinite(createdMs) && createdMs > storedMs;
        }).length;
        setEmailLeadUnreadCount(nextCount);
      } catch {
        if (!cancelled) setEmailLeadUnreadCount(0);
      }
    };

    void refreshEmailLeadUnread();
    const timer = window.setInterval(refreshEmailLeadUnread, 30000);
    const syncSeen = () => void refreshEmailLeadUnread();
    window.addEventListener("storage", syncSeen);
    window.addEventListener("focus", syncSeen);
    window.addEventListener(EMAIL_CAPTURE_SEEN_EVENT, syncSeen);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("storage", syncSeen);
      window.removeEventListener("focus", syncSeen);
      window.removeEventListener(EMAIL_CAPTURE_SEEN_EVENT, syncSeen);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(max-width: 899px)");
    const handleChange = () => setIsMobileViewport(media.matches);
    handleChange();
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const content = await ContentAPI.get();
        if (cancelled) return;
        setBrand({
          siteName: String(content.siteName || "BeyondPendants").trim() || "BeyondPendants",
          logoUrl: assetUrl(content.logoUrl) || "",
        });
      } catch {
        // keep default branding
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setOpen(false);
    }
  }, [isMobileViewport]);

  useEffect(() => {
    if (isMobileViewport) {
      setOpen(false);
    }
  }, [isMobileViewport, pathname]);

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100dvh",
        background: "#f8fafc",
      }}
    >
      <Sidebar
        isOpen={open}
        onClose={() => setOpen(false)}
        supportUnreadCount={supportUnreadCount}
        emailLeadUnreadCount={emailLeadUnreadCount}
        isMobile={isMobileViewport}
        brandName={brand.siteName}
        brandLogoUrl={brand.logoUrl}
      />
      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          minHeight: "100dvh",
        }}
      >
        <header
          style={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            background: "rgba(255,255,255,0.88)",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: isMobileViewport ? "12px" : "14px 20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              minWidth: 0,
            }}
          >
            {isMobileViewport ? (
              <button
                onClick={() => setOpen((v) => !v)}
                aria-label="Toggle menu"
                style={{
                  border: "1px solid #cbd5e1",
                  background: "#fff",
                  padding: "8px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 800,
                }}
              >
                ☰
              </button>
            ) : null}
            <Link
              to="/"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
                textDecoration: "none",
              }}
            >
              <div
                style={{
                  width: 104,
                  height: 42,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {brand.logoUrl ? (
                  <img
                    src={brand.logoUrl}
                    alt={brand.siteName}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <span>{brand.siteName.slice(0, 1).toUpperCase()}</span>
                )}
              </div>
            </Link>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 10,
              flexWrap: "wrap",
              fontSize: 12,
              color: "#475569",
            }}
          >
            {!isMobileViewport ? (
              <span
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  background: "#eff6ff",
                  color: "#1e3a8a",
                  border: "1px solid #bfdbfe",
                  fontWeight: 700,
                }}
              >
                {pathname}
              </span>
            ) : null}
            {supportUnreadCount > 0 ? (
              <span
                style={{
                  padding: "4px 8px",
                  borderRadius: 6,
                  background: "#fee2e2",
                  color: "#991b1b",
                  border: "1px solid #fecaca",
                  fontWeight: 700,
                }}
              >
                {supportUnreadCount} new chat{supportUnreadCount === 1 ? "" : "s"}
              </span>
            ) : null}
            {user ? (
              <span style={{ color: "#0f172a", fontWeight: 600 }}>
                {user.fullName} ({user.role === "super_admin" ? "Super Admin" : "Manager"})
              </span>
            ) : null}
            <button
              type="button"
              onClick={logout}
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: 6,
                background: "#fff",
                padding: "6px 9px",
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Sign Out
            </button>
          </div>
        </header>
        <div
          style={{
            width: "100%",
            maxWidth: "none",
            margin: 0,
            padding: isMobileViewport ? 12 : 24,
            boxSizing: "border-box",
          }}
        >
          {children}
        </div>
      </main>
    </div>
  );
}
